/**
 * POLICY AZIENDALI — un posto solo per le regole che l'azienda impone ai dipendenti.
 *
 * Le tre famiglie che stanno qui (ritardi, smart working, pianificazione) le apre la stessa
 * persona nello stesso momento — il setup dell'azienda, o il giorno in cui una regola cambia —
 * ed erano tre voci di menu distanti fra loro. Il criterio del raggruppamento è quello: **chi
 * le configura**, non il dominio a cui appartengono.
 *
 * ## Cosa NON sta qui, e perché
 *
 * · **Policy trasferte** (`/admin/travel-policies`): non ha un ambito, si assegna la fascia al
 *   singolo dipendente con una data di inizio (`travel-policy-tiers`). È una pagina di
 *   assegnazioni, ha già due schede sue, e vive dietro il flag `travel.enabled`.
 * · **Configurazione assenze** (`/admin/leave-config`): è un catalogo (tipi, motivazioni,
 *   blackout), non una regola che il server risolve per una persona. Ha già tre schede sue.
 *
 * Metterle qui avrebbe prodotto otto sezioni su due livelli di schede, che è il punto in cui
 * l'utente smette di sapere dove si trova.
 *
 * ## Le vecchie rotte restano
 *
 * `/admin/tardiness-policies`, `/admin/smart-working-config` e `/admin/planning-policies`
 * continuano a funzionare e rendono lo stesso pannello a pagina piena: sono nei segnalibri e
 * nella documentazione. Cambia il **menu**, che ora ha una voce sola. I pannelli stanno in
 * `features/`, e questa pagina come quelle li rende con `embedded`.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, Text } from '@mantine/core';
import { IconCalendarCog, IconGavel, IconHistory, IconHome, IconVideo } from '@tabler/icons-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { hasClaim } from '@/shared/permissions/claims';
import { useHashTab } from '@/shared/hooks/useHashTab';
import { MaeShell, MaePageHead, MaeTabs, type MaeTabDef } from '@/shared/desktop/MaeShell';
import { TardinessPoliciesPanel } from '@/features/working-hours/TardinessPoliciesPanel';
import { PlanningPoliciesPanel } from '@/features/planning/PlanningPoliciesPanel';
import { CalendarIntegrationPanel } from '@/features/planning/CalendarIntegrationPanel';

export const Route = createFileRoute('/_app/admin/policies')({ component: PoliciesPage });

type Tab = 'ritardi' | 'smart-working' | 'pianificazione' | 'calendario';

/** Fuori dal componente: `useHashTab` la usa come dipendenza di un effetto. */
const TAB_VALUES: readonly Tab[] = ['ritardi', 'smart-working', 'pianificazione', 'calendario'] as const;

/** Il claim che permette di *configurare* ciascuna famiglia — gli stessi di `appNav`. */
const TAB_CLAIMS: Record<Tab, readonly string[]> = {
  ritardi: ['TardinessPolicy.update'],
  'smart-working': ['SmartWorkingPolicy.update'],
  pianificazione: ['PlanningPolicy.update'],
  /* La quarta famiglia (09-09-2026): l'integrazione calendario e le stanze di riunione degli impegni
     pianificati. Sta qui e non in «Integrazioni» perché quella pagina è il consenso della persona,
     questa è la regola dell'azienda — e la configura chi configura la pianificazione. */
  calendario: ['CalendarIntegrationSetting.update'],
};

function PoliciesPage() {
  const { decoded } = useAuth();
  const claims = Array.isArray(decoded?.claims) ? (decoded.claims as string[]) : [];
  const isSuper = decoded?.superadmin === 'True';
  const can = (tab: Tab) => isSuper || TAB_CLAIMS[tab].some((c) => hasClaim(claims, c));

  const [hash, setHash] = useHashTab('ritardi', TAB_VALUES);
  /* Chi ha il permesso di una famiglia sola non deve atterrare su una scheda che non può
     aprire: il segnalibro `#ritardi` di un collega non gli lascerebbe niente da vedere. */
  const request = hash as Tab;
  const active = can(request) ? request : TAB_VALUES.find(can) ?? request;

  const tabs: MaeTabDef<Tab>[] = [
    { value: 'ritardi', label: 'Ritardi', icon: <IconHistory size={15} />, disabled: !can('ritardi') },
    { value: 'smart-working', label: 'Smart working', icon: <IconHome size={15} />, disabled: !can('smart-working') },
    { value: 'pianificazione', label: 'Pianificazione', icon: <IconCalendarCog size={15} />, disabled: !can('pianificazione') },
    { value: 'calendario', label: 'Calendario e riunioni', icon: <IconVideo size={15} />, disabled: !can('calendario') },
  ];

  return (
    <MaeShell>
      {/*
        La sola cosa che le tre schede, prese una per una, non dicevano: che «per chi vale»
        funziona in due modi diversi. Affiancate, la differenza si vede — ed è quella che
        spiega perché una policy che sembra giusta non si applica alla persona attesa.

        Era un riquadro azzurro fisso sotto l'intestazione: si legge una volta e poi si scavalca
        per sempre, occupando il posto dove comincia il contenuto. Ora sta nell'icona ⓘ accanto
        al titolo — stesso testo, nessuna riga di pagina.
      */}
      <MaePageHead
        icon={<IconGavel size={22} />}
        title="Policy aziendali"
        sub="Le regole che l’azienda applica alle timbrature e alle richieste dei dipendenti: ritardi, smart working, pianificazione, calendario e riunioni."
        helpTitle="Per chi vale una policy"
        help={(
          <>
            <b>Ritardi</b> e <b>pianificazione</b> hanno un ambito a cinque gradini — azienda, sede,
            gruppo, sede+gruppo, dipendente — e il sistema applica <b>la più specifica</b>, non la
            somma. Lo <b>smart working</b> non ha gerarchia: ogni policy vale per l’azienda o per il
            gruppo societario, con una sola <i>predefinita</i> e una finestra di validità.
          </>
        )}
      />

      <div style={{ marginTop: 16 }}>
        <MaeTabs value={active} onChange={(v) => setHash(v)} tabs={tabs} mb={18} />
      </div>

      {active === 'ritardi' && <TardinessPoliciesPanel embedded />}

      {/* Dall'08-10-2026 lo smart working sta tutto in «Regole di lavoro → Modalità di lavoro»,
          regole e impostazioni insieme, e l'ufficio del personale ci entra. Qui resta la scheda,
          perché è dove molti lo cercano, ma porta là: due porte sulle stesse regole dicono, prima
          o poi, due cose diverse. */}
      {active === 'smart-working' && (
        <Card withBorder radius="lg" padding="md" shadow="sm">
          <Text size="sm">
            Le regole del lavoro da casa — policy, periodi bloccati, presidio minimo, ricorrenze —
            stanno ora insieme alle impostazioni del modulo, in{' '}
            <Link to="/organization/work-rules" search={{ section: 'work-modes' }}>
              Regole di lavoro → Modalità di lavoro
            </Link>.
          </Text>
        </Card>
      )}

      {active === 'pianificazione' && <PlanningPoliciesPanel embedded />}

      {active === 'calendario' && <CalendarIntegrationPanel embedded />}

      {/* Chi cerca «le policy» cerca anche queste due: da qualche parte va detto dove sono. */}
      <Text size="xs" c="dimmed" mt={20}>
        Non sono qui: le <Link to="/admin/travel-policies">policy trasferte</Link> (si assegnano
        per dipendente, non per ambito) e la{' '}
        <Link to="/admin/leave-config">configurazione delle assenze</Link> (è un catalogo di tipi
        e motivazioni, non una regola risolta dal sistema).
      </Text>
    </MaeShell>
  );
}
